import { useParams, useNavigate } from "react-router-dom";
import { actingGigs } from "../../data/actingData";

function Details() {
  const { gigName } = useParams<{ gigName: string }>();
  const navigate = useNavigate();
  
  const gig = actingGigs.find(g => 
    g.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') === gigName
  );

  if (!gig) {
    return (
      <main className="flex-1 w-full px-4 md:px-8 py-12 max-w-7xl mx-auto">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4 text-gray-300">Project Not Found</h1>
          <button
            onClick={() => navigate("/acting")}
            className="px-6 py-3 bg-yellow-500 text-gray-900 font-bold rounded-lg hover:bg-yellow-400 transition-all"
          >
            Back to Gallery
          </button>
        </div>
      </main>
    );
  }

  const getVideoId = (url: string): string | null => {
    if (!url) return null;
    
    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (youtubeMatch) return youtubeMatch[1];
    
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `vimeo:${vimeoMatch[1]}`;
    
    return null;
  };

  const getEmbedUrl = (url: string): string | null => {
    const videoId = getVideoId(url);
    if (!videoId) return null;
    
    if (videoId.startsWith('vimeo:')) {
      const vimeoId = videoId.replace('vimeo:', '');
      return `https://player.vimeo.com/video/${vimeoId}`;
    }
    
    return `https://www.youtube.com/embed/${videoId}`;
  };

  return (
    <main className="flex-1 w-full px-4 md:px-8 py-12 max-w-7xl mx-auto animate-fadeIn">
      {/* Back Button */}
      <button
        onClick={() => navigate("/acting")}
        className="mb-8 inline-flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-all hover:translate-x-[-4px]"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Gallery
      </button>

      {/* Header */}
      <div className="mb-8 animate-slideDown">
        <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-400 via-yellow-500 to-amber-500 bg-clip-text text-transparent">
          {gig.name}
        </h1>
        
        <div className="flex flex-wrap gap-4 mb-4">
          {gig.year && (
            <span className="px-3 py-1 bg-gray-800 text-gray-300 rounded-full text-sm">
              {gig.year}
            </span>
          )}
          <span className="px-3 py-1 bg-yellow-500 text-gray-900 rounded-full text-sm font-medium">
            {gig.category}
          </span>
          {gig.role && (
            <span className="px-3 py-1 bg-gray-800 text-gray-300 rounded-full text-sm">
              Role: {gig.role}
            </span>
          )}
        </div>

        {gig.description && (
          <p className="text-lg text-gray-300 max-w-3xl mb-6">
            {gig.description}
          </p>
        )}

        {/* Links */}
        <div className="flex flex-wrap gap-4">
          {gig.youtube && (
            <a
              href={gig.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-all hover:scale-105 transform"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              Watch on YouTube
            </a>
          )}
          {gig.imdb && (
            <a
              href={gig.imdb}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-gray-900 rounded-lg transition-all hover:scale-105 transform font-bold"
            >
              <span>IMDb</span>
            </a>
          )}
        </div>
      </div>

      {/* Video Player */}
      {gig.youtube && (
        <div className="mb-12 bg-gray-900 rounded-xl overflow-hidden shadow-2xl animate-scaleIn">
          <div className="relative pt-[56.25%]">
            <iframe
              src={getEmbedUrl(gig.youtube) || ""}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Image Gallery */}
      {gig.images && gig.images.length > 0 && (
        <div className="mb-12 animate-slideUp">
          <h2 className="text-2xl font-bold text-gray-300 mb-6">Behind the Scenes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {gig.images.map((image, index) => (
              <div 
                key={index}
                className="relative bg-gray-900 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all transform hover:scale-105 animate-fadeIn"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <img
                  src={image}
                  alt={`${gig.name} - Image ${index + 1}`}
                  className="w-full h-64 object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </main>
  );
}

export default Details;
